import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image, Link } from '@react-pdf/renderer';
import { WorksheetState } from '@src/stores/worksheet.store';

type Question = {
  title: string;
  imageSrc: string;
  markSchemeSrc?: string;
  writtenMsSrc?: string;
  markSchemeUrl?: string;
  writtenMsUrl?: string;
  categories?: unknown[];
  areas?: any[];
  subtopics?: any[];
};

type Worksheet = {
  questions: Question[];
  cover?: WorksheetState['cover'];
  type: 'worksheet' | 'markScheme';
};

const hasLength = (arr: any[]) => arr?.length > 0;

const styles = StyleSheet.create({
  // Use a column layout for the cover page
  coverPage: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    position: 'relative',
  },
  header: {
    position: 'absolute',
    top: 10,
    right: 10,
    fontSize: 9,
    fontStyle: 'italic',
  },
  coverSection: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  coverTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    textAlign: 'center',
    textDecoration: 'underline',
    marginBottom: 10,
  },
  coverText: {
    fontSize: 12,
    marginTop: 5,
  },
  page: {
    flexDirection: 'row',
  },
  section: {
    margin: 10,
    padding: 10,
    flexGrow: 1,
  },
  title: {
    fontSize: 12,
    marginBottom: 10,
    textDecoration: 'underline',
  },
  subText: {
    fontSize: 7,
    marginBottom: 5,
  },
  imageContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    marginBottom: 10,
  },
  imageQ: {
    width: '1000',
    objectFit: 'contain',
  },
  imageM: {
    width: 1000,
    height: '80%',
    objectFit: 'contain',
  },
  qrContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    position: 'absolute',
    bottom: 10,
    left: 0,
    right: 0,
    padding: '0 10',
    width: '100%',
  },
  qrDiv: {
    fontSize: 8,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 5,
  },
  qrImage: {
    width: 75,
    height: 75,
  },
  subTextSpan: {
    color: '#6b6b6b',
  },
});

const WorksheetPDF = ({ questions, cover }: Worksheet) => (
  <Document>
    {/* Cover Page */}
    {cover && (
      <Page size="A4" style={styles.coverPage}>
        <Image
          src="https://www.datocms-assets.com/34817/1738836657-corner-graphic.png"
          style={{
            width: 100,
            height: 100,
            position: 'absolute',
            top: 0,
            right: 0,
          }}
        />
        <View style={styles.coverSection}>
          {cover.title && <Text style={styles.coverTitle}>{cover.title}</Text>}
          {cover.subtitle && <Text style={styles.coverText}>{cover.subtitle}</Text>}
          {cover.comments && <Text style={styles.coverText}>{cover.comments}</Text>}
          {cover.timeAllowed && <Text style={styles.coverText}>Time: {cover.timeAllowed}</Text>}
          {cover.totalMarks && <Text style={styles.coverText}>Marks: {cover.totalMarks}</Text>}
          {cover.startDate && <Text style={styles.coverText}>{cover.startDate}</Text>}
        </View>
      </Page>
    )}

    {/* Question Pages */}
    {questions.map((question, index) => {
      const codes = [
        { title: 'Mark Scheme', src: question.markSchemeSrc, url: question.markSchemeUrl },
        { title: 'Written Mark Scheme', src: question.writtenMsSrc, url: question.writtenMsUrl },
      ];
      console.log(codes);
      const includeCodes = codes.some((code) => code.src);

      return (
        <Page key={index} size="A4" style={styles.page}>
          <View style={styles.section}>
            <Text style={styles.header}>Created at mymathscloud.com</Text>
            <Text style={styles.title}>{question.title}</Text>

            {/* Categories */}
            {hasLength(question.areas) && (
              <Text style={styles.subText}>
                Areas: <Text style={styles.subTextSpan}>{question.areas.join(', ')}</Text>
              </Text>
            )}

            {hasLength(question.subtopics) && (
              <Text style={styles.subText}>
                Subtopics: <Text style={styles.subTextSpan}>{question.subtopics.join(', ')}</Text>
              </Text>
            )}

            {hasLength(question.categories) && (
              <Text style={styles.subText}>{question.categories.join(' / ')}</Text>
            )}

            {/* Image */}
            <Image
              src={question.imageSrc}
              style={{
                maxHeight: 675,
                margin: '0 auto',
                width: 450,
                // objectFit: 'contain',
              }}
            />

            {/* QR Codes */}
            {includeCodes && (
              <View style={styles.qrContainer}>
                {codes.map((code, idx) => {
                  if (!code.url) return null;
                  return (
                    <View key={idx} style={styles.qrDiv}>
                      <Text>Scan Me!</Text>
                      <Image src={code.src} style={styles.qrImage} />
                      <Text>{code.title}</Text>
                      <Link src={code.url}>View</Link>
                    </View>
                  );
                })}
              </View>
            )}
          </View>
        </Page>
      );
    })}
  </Document>
);

export default WorksheetPDF;
